import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n/LanguageContext";
import SubscriptionsAPI from "../services/SubscriptionsAPI";
import { useUser } from "../context/UserContext";
import { useSubscription } from "../context/SubscriptionContext";
import { useEdition } from "../context/EditionContext";
import { getPlanFeatures } from "../utils/planDescriptions";
import "./css/ServicePlans.css";
import LoadingSpinner from "../common/LoadingSpinner";

// Corporate (Teams/Business) plans surfaced to users. The backend seeds five
// flat-fee SMB bands (smb-10/25/50/75/100); we display only these three so the
// plan grid stays a manageable width.
const VISIBLE_CORPORATE_PLANS = ["smb-25", "smb-50", "smb-100"];

// Resolve a plan's edition robustly. Prefer the explicit `edition` field, but
// fall back to detecting the smb-* plan-name prefix so corporate plans are never
// misclassified as CONSUMER (and leaked into the consumer plan picker) if the
// API response omits `edition`. Mirrors the heuristic used in EditionContext.
const resolvePlanEdition = (rawEdition, planName) => {
  if (rawEdition) return String(rawEdition).toUpperCase();
  if (
    String(planName || "")
      .toLowerCase()
      .startsWith("smb-")
  )
    return "CORPORATE";
  return "CONSUMER";
};

const ServicePlans = ({
  onPlanSelected,
  selectedPlan,
  showBackButton = false,
  onBack,
  isModal = false,
  onClose,
}) => {
  const { loggedInUser } = useUser();
  const {
    subscription,
    servicePlan,
    hasActiveSubscription,
    isLoading: contextLoading,
  } = useSubscription();
  const { edition, term } = useEdition();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(selectedPlan || null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentPlanLoading, setCurrentPlanLoading] = useState(true);
  const { t } = useTranslation();

  // Default plans based on the database schema
  const defaultPlans = [
    {
      plan_id: 1,
      plan_name: "individual",
      display_name: "Individual",
      description:
        "Perfect for individuals with full featured security awareness training needs.",
      price_monthly: 9.99,
      price_yearly: 99.99,
      max_users: 1,
      max_groups: 1,
      max_simulations_per_month: 60,
      max_reports_per_month: 2,
      max_storage_gb: 1,
      has_advanced_analytics: false,
      has_custom_training: false,
      has_priority_support: false,
      has_api_access: false,
      has_sso_integration: false,
      has_custom_branding: false,
      has_audit_logs: false,
      has_advanced_reporting: false,
      is_popular: false,
      sort_order: 1,
      trial_period_days: 30,
      edition: "CONSUMER",
    },
    {
      plan_id: 2,
      plan_name: "family",
      display_name: "Family",
      description: "Ideal for families wanting to protect their household.",
      price_monthly: 19.99,
      price_yearly: 199.99,
      max_users: 3,
      max_groups: 2,
      max_simulations_per_month: 200,
      max_reports_per_month: 10,
      max_storage_gb: 5,
      has_advanced_analytics: true,
      has_custom_training: false,
      has_priority_support: true,
      has_api_access: false,
      has_sso_integration: false,
      has_custom_branding: false,
      has_audit_logs: false,
      has_advanced_reporting: true,
      is_popular: true,
      sort_order: 2,
      trial_period_days: 30,
      edition: "CONSUMER",
    },
    {
      plan_id: 3,
      plan_name: "extended_family",
      display_name: "Extended Family",
      description:
        "Complete protection for larger families or small groups with advanced features and priority support.",
      price_monthly: 29.99,
      price_yearly: 299.99,
      max_users: 6,
      max_groups: 2,
      max_simulations_per_month: 400,
      max_reports_per_month: 50,
      max_storage_gb: 25,
      has_advanced_analytics: true,
      has_custom_training: true,
      has_priority_support: true,
      has_api_access: true,
      has_sso_integration: true,
      has_custom_branding: false,
      has_audit_logs: true,
      has_advanced_reporting: true,
      is_popular: false,
      sort_order: 3,
      trial_period_days: 30,
      edition: "CONSUMER",
    },
    {
      plan_id: 4,
      plan_name: "smb-10",
      display_name: "Team (up to 10)",
      description:
        "Security awareness training and scam protection for small teams of up to 10 employees.",
      price_monthly: 70.0,
      price_yearly: 700.0,
      max_users: 10,
      max_groups: 3,
      max_simulations_per_month: 400,
      max_reports_per_month: 40,
      max_storage_gb: 25,
      has_advanced_analytics: true,
      has_custom_training: true,
      has_priority_support: true,
      has_api_access: false,
      has_sso_integration: false,
      has_custom_branding: false,
      has_audit_logs: true,
      has_advanced_reporting: true,
      is_popular: false,
      sort_order: 10,
      trial_period_days: 14,
      edition: "CORPORATE",
    },
    {
      plan_id: 5,
      plan_name: "smb-25",
      display_name: "Team (up to 25)",
      description:
        "Security awareness training and scam protection for small teams of up to 25 employees.",
      price_monthly: 160.0,
      price_yearly: 1600.0,
      max_users: 25,
      max_groups: 5,
      max_simulations_per_month: 1000,
      max_reports_per_month: 100,
      max_storage_gb: 50,
      has_advanced_analytics: true,
      has_custom_training: true,
      has_priority_support: true,
      has_api_access: false,
      has_sso_integration: false,
      has_custom_branding: false,
      has_audit_logs: true,
      has_advanced_reporting: true,
      is_popular: false,
      sort_order: 11,
      trial_period_days: 14,
      edition: "CORPORATE",
    },
    {
      plan_id: 6,
      plan_name: "smb-50",
      display_name: "Business (up to 50)",
      description:
        "Comprehensive protection and training for growing businesses of up to 50 employees.",
      price_monthly: 300.0,
      price_yearly: 3000.0,
      max_users: 50,
      max_groups: 10,
      max_simulations_per_month: 2000,
      max_reports_per_month: 200,
      max_storage_gb: 100,
      has_advanced_analytics: true,
      has_custom_training: true,
      has_priority_support: true,
      has_api_access: false,
      has_sso_integration: false,
      has_custom_branding: false,
      has_audit_logs: true,
      has_advanced_reporting: true,
      is_popular: true,
      sort_order: 12,
      trial_period_days: 14,
      edition: "CORPORATE",
    },
    {
      plan_id: 7,
      plan_name: "smb-75",
      display_name: "Business (up to 75)",
      description:
        "Comprehensive protection and training for businesses of up to 75 employees.",
      price_monthly: 420.0,
      price_yearly: 4200.0,
      max_users: 75,
      max_groups: 15,
      max_simulations_per_month: 3000,
      max_reports_per_month: 300,
      max_storage_gb: 175,
      has_advanced_analytics: true,
      has_custom_training: true,
      has_priority_support: true,
      has_api_access: false,
      has_sso_integration: false,
      has_custom_branding: false,
      has_audit_logs: true,
      has_advanced_reporting: true,
      is_popular: false,
      sort_order: 13,
      trial_period_days: 14,
      edition: "CORPORATE",
    },
    {
      plan_id: 8,
      plan_name: "smb-100",
      display_name: "Business Plus (up to 100)",
      description:
        "Full-featured security awareness program for organizations of up to 100 employees.",
      price_monthly: 500.0,
      price_yearly: 5000.0,
      max_users: 100,
      max_groups: 20,
      max_simulations_per_month: 4000,
      max_reports_per_month: 400,
      max_storage_gb: 250,
      has_advanced_analytics: true,
      has_custom_training: true,
      has_priority_support: true,
      has_api_access: false,
      has_sso_integration: false,
      has_custom_branding: false,
      has_audit_logs: true,
      has_advanced_reporting: true,
      is_popular: false,
      sort_order: 14,
      trial_period_days: 14,
      edition: "CORPORATE",
    },
  ];

  useEffect(() => {
    fetchServicePlans();
  }, [loggedInUser]);

  // Derive currentPlan from SubscriptionContext (single source of truth)
  useEffect(() => {
    if (contextLoading) {
      setCurrentPlanLoading(true);
      return;
    }

    if (hasActiveSubscription && subscription) {
      let planId = subscription.planId
        ? parseInt(subscription.planId, 10)
        : null;

      // If no planId but we have a planName, try to match it to our default plans
      if (!planId && (subscription.planName || subscription.planDisplayName)) {
        const planName = subscription.planName || subscription.planDisplayName;
        const matchingPlan = defaultPlans.find(
          (plan) =>
            plan.plan_name.toLowerCase() === planName.toLowerCase() ||
            plan.display_name.toLowerCase() === planName.toLowerCase(),
        );
        if (matchingPlan) {
          planId = matchingPlan.plan_id;
        }
      }

      // Also try matching via servicePlan from context
      if (!planId && servicePlan) {
        planId = servicePlan.planId ? parseInt(servicePlan.planId, 10) : null;
      }

      if (planId) {
        const currentPlanData = {
          plan_id: planId,
          plan_name:
            servicePlan?.planName ||
            subscription.planName ||
            subscription.planType,
          display_name:
            servicePlan?.displayName ||
            subscription.planDisplayName ||
            subscription.planName ||
            "Current Plan",
        };
        console.log(
          "✅ ServicePlans: Current plan derived from SubscriptionContext:",
          currentPlanData,
        );
        setCurrentPlan(currentPlanData);
      } else {
        console.log(
          "⚠️ ServicePlans: Could not determine plan ID from SubscriptionContext",
        );
        setCurrentPlan(null);
      }
    } else {
      console.log(
        "ℹ️ ServicePlans: No active subscription in SubscriptionContext",
      );
      setCurrentPlan(null);
    }
    setCurrentPlanLoading(false);
  }, [subscription, servicePlan, hasActiveSubscription, contextLoading]);

  const fetchServicePlans = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to fetch from API, fallback to default plans
      try {
        const response = await SubscriptionsAPI.getAllServicePlans();
        // Handle both array response and { data: [...] } response format
        const apiPlans = Array.isArray(response)
          ? response
          : response?.data || response;
        if (Array.isArray(apiPlans) && apiPlans.length > 0) {
          // Normalize API response to use snake_case field names (handle both camelCase and snake_case)
          const normalizedPlans = apiPlans.map((plan) => ({
            plan_id: plan.plan_id ?? plan.planId,
            plan_name: plan.plan_name ?? plan.planName,
            display_name: plan.display_name ?? plan.displayName,
            description: plan.description,
            price_monthly: plan.price_monthly ?? plan.priceMonthly ?? 0,
            price_yearly: plan.price_yearly ?? plan.priceYearly ?? 0,
            max_users: plan.max_users ?? plan.maxUsers ?? 1,
            max_groups: plan.max_groups ?? plan.maxGroups ?? 1,
            max_simulations_per_month:
              plan.max_simulations_per_month ?? plan.maxSimulationsPerMonth,
            max_reports_per_month:
              plan.max_reports_per_month ?? plan.maxReportsPerMonth,
            max_storage_gb: plan.max_storage_gb ?? plan.maxStorageGb,
            has_advanced_analytics:
              plan.has_advanced_analytics ?? plan.hasAdvancedAnalytics ?? false,
            has_custom_training:
              plan.has_custom_training ?? plan.hasCustomTraining ?? false,
            has_priority_support:
              plan.has_priority_support ?? plan.hasPrioritySupport ?? false,
            has_api_access: plan.has_api_access ?? plan.hasApiAccess ?? false,
            has_sso_integration:
              plan.has_sso_integration ?? plan.hasSsoIntegration ?? false,
            has_custom_branding:
              plan.has_custom_branding ?? plan.hasCustomBranding ?? false,
            has_audit_logs: plan.has_audit_logs ?? plan.hasAuditLogs ?? false,
            has_advanced_reporting:
              plan.has_advanced_reporting ?? plan.hasAdvancedReporting ?? false,
            is_popular: plan.is_popular ?? plan.isPopular ?? false,
            sort_order: plan.sort_order ?? plan.sortOrder ?? 0,
            trial_period_days:
              plan.trial_period_days ?? plan.trialPeriodDays ?? 0,
            tier: plan.tier,
            edition: resolvePlanEdition(
              plan.edition,
              plan.plan_name ?? plan.planName,
            ),
          }));
          setPlans(
            normalizedPlans.sort(
              (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
            ),
          );
        } else {
          console.log("Using default plans (empty API response)");
          setPlans(defaultPlans);
        }
      } catch (apiError) {
        console.log("Using default plans (API not available)", apiError);
        setPlans(defaultPlans);
      }
    } catch (err) {
      console.error("Error fetching service plans:", err);
      setError(t("servicePlans.error.loadFailed"));
      setPlans(defaultPlans); // Use default plans as fallback
    } finally {
      setLoading(false);
    }
  };
  const handlePlanSelection = (plan) => {
    setSelectedPlanId(plan.plan_id);
    if (onPlanSelected) {
      onPlanSelected(plan);
    }
    // If this is a modal, close it after selection
    if (isModal && onClose) {
      onClose();
    }
  };

  const subscribeToplan = async (planId) => {
    try {
      const result = await SubscriptionsAPI.subscribeToPlan({
        plan_id: planId,
        payment_cycle: "monthly", // Default to monthly
      });

      return result;
    } catch (error) {
      console.error("Error subscribing to plan:", error);
      throw error;
    }
  };

  const upgradePlan = async () => {
    if (!currentPlan) return;

    const nextPlan = plans.find(
      (plan) => plan.sort_order === currentPlan.sort_order + 1,
    );

    if (!nextPlan) {
      console.log("No higher plan available");
      return;
    }

    try {
      const result = await subscribeToplan(nextPlan.plan_id);
      setCurrentPlan(result);
      setSelectedPlanId(result.plan_id);
      toast.success(
        `${t("servicePlans.toast.upgraded")} ${nextPlan.display_name}`,
      );
    } catch (error) {
      console.error("Error upgrading plan:", error);
    }
  };

  const downgradePlan = async () => {
    if (!currentPlan) return;

    const previousPlan = plans.find(
      (plan) => plan.sort_order === currentPlan.sort_order - 1,
    );

    if (!previousPlan) {
      console.log("No lower plan available");
      return;
    }

    try {
      const result = await subscribeToplan(previousPlan.plan_id);
      setCurrentPlan(result);
      setSelectedPlanId(result.plan_id);
      toast.success(
        `${t("servicePlans.toast.downgraded")} ${previousPlan.display_name}`,
      );
    } catch (error) {
      console.error("Error downgrading plan:", error);
    }
  };

  const formatPrice = (monthly, yearly) => {
    if (monthly === 0) return t("servicePlans.price.free");
    return `$${monthly.toFixed(2)}${t("servicePlans.price.perMonth")}`;
  };

  const formatYearlyPrice = (yearly) => {
    if (yearly === 0) return null;
    return `$${yearly.toFixed(2)}${t("servicePlans.price.perYear")}`;
  };

  const getFeaturesList = (plan) => {
    // Use the shared marketing features from planDescriptions.js
    return getPlanFeatures(plan.plan_name || plan.plan_id, t);
  };
  // Check if there's a pending downgrade
  const pendingPlanId = subscription?.pendingPlanId
    ? parseInt(subscription.pendingPlanId, 10)
    : null;
  const pendingPlanChangeDate = subscription?.pendingPlanChangeDate || null;

  // Determine if a plan is an upgrade or downgrade compared to current plan
  const getPlanRelationship = (plan) => {
    if (!currentPlan || currentPlanLoading) return "select";

    const planId = parseInt(plan.plan_id, 10);
    const currentPlanId = parseInt(currentPlan.plan_id, 10);

    if (planId === currentPlanId) return "current";

    // If this plan is the pending downgrade target, mark it
    if (pendingPlanId && planId === pendingPlanId) return "pending_downgrade";

    // Simple upgrade/downgrade logic based on plan_id (assuming higher ID = higher tier)
    if (planId > currentPlanId) return "upgrade";
    if (planId < currentPlanId) return "downgrade";

    return "select";
  };

  // Get button text based on plan relationship
  const getButtonText = (plan) => {
    const relationship = getPlanRelationship(plan);

    switch (relationship) {
      case "current":
        return t("servicePlans.btn.current");
      case "pending_downgrade":
        return t("servicePlans.btn.downgradePending");
      case "upgrade":
        return t("servicePlans.btn.upgrade");
      case "downgrade":
        return t("servicePlans.btn.downgrade");
      default:
        return selectedPlanId === plan.plan_id
          ? t("servicePlans.btn.selected")
          : t("servicePlans.btn.select");
    }
  };
  // Check if a plan card should be disabled
  const isPlanDisabled = (plan) => {
    const planIdNum = parseInt(plan.plan_id, 10);
    // Disable current plan
    const isCurrent =
      currentPlan && planIdNum === parseInt(currentPlan.plan_id, 10);
    // Disable plan that's already the pending downgrade target
    const isPending = pendingPlanId && planIdNum === pendingPlanId;

    if (isCurrent) {
      console.log(
        `Plan ${plan.display_name} (ID: ${plan.plan_id}) is disabled - matches current plan (ID: ${currentPlan.plan_id})`,
      );
    }
    if (isPending) {
      console.log(
        `Plan ${plan.display_name} (ID: ${plan.plan_id}) is disabled - pending downgrade target`,
      );
    }
    return isCurrent || isPending;
  };

  if (loading) {
    return (
      <div className="service-plans-container">
        <LoadingSpinner message={t("servicePlans.loading")} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="service-plans-container">
        <div className="plans-error">
          <h2>{t("servicePlans.errorTitle")}</h2>
          <p>{error}</p>
          <button className="retry-button" onClick={fetchServicePlans}>
            {t("servicePlans.tryAgain")}
          </button>
        </div>
      </div>
    );
  }
  const renderPlansContent = () => (
    <div className={`service-plans-container ${isModal ? "modal-mode" : ""}`}>
      {/* Modal Header with Close Button */}
      {isModal && (
        <div className="modal-header">
          <h1>{term("plansTitle")}</h1>
          <button className="modal-close-button" onClick={onClose}>
            ×
          </button>
        </div>
      )}

      {!isModal && (
        <div className="plans-header">
          <h1 className="main-title">{term("plansTitle")}</h1>
          <p>{t("servicePlans.pageSubtitle")}</p>
        </div>
      )}

      <div className="plans-grid">
        {plans
          .filter((plan) => plan.is_active !== false) // Only show active plans
          .filter(
            (plan) =>
              (plan.edition ? plan.edition.toUpperCase() : "CONSUMER") ===
              edition,
          ) // Only show plans for the current account edition (consumer vs corporate)
          .filter(
            (plan) =>
              (plan.edition ? plan.edition.toUpperCase() : "CONSUMER") !==
                "CORPORATE" || VISIBLE_CORPORATE_PLANS.includes(plan.plan_name),
          ) // Limit the corporate (Teams/Business) view to 3 plans so the grid stays a manageable width
          .map((plan) => (
            <div
              key={plan.plan_id}
              className={`plan-card ${
                selectedPlanId === plan.plan_id ? "selected" : ""
              } ${plan.is_popular ? "popular" : ""} ${
                isPlanDisabled(plan) ? "disabled" : ""
              }`}
              onClick={() => !isPlanDisabled(plan) && handlePlanSelection(plan)}
            >
              {plan.is_popular && !isPlanDisabled(plan) && (
                <div className="popular-badge">
                  {t("servicePlans.badge.popular")}
                </div>
              )}

              {isPlanDisabled(plan) && (
                <div
                  className={`current-plan-badge ${
                    pendingPlanId &&
                    parseInt(plan.plan_id, 10) === pendingPlanId
                      ? "pending-badge"
                      : ""
                  }`}
                >
                  {pendingPlanId && parseInt(plan.plan_id, 10) === pendingPlanId
                    ? `${t("servicePlans.badge.switching")} ${pendingPlanChangeDate ? pendingPlanChangeDate : t("servicePlans.badge.soon")}`
                    : t("servicePlans.badge.current")}
                </div>
              )}

              <div className="plan-content">
                <div className="plan-header">
                  <h3>{t(`servicePlans.plan.${plan.plan_name}.name`)}</h3>
                  <div className="plan-price">
                    <span className="price-monthly">
                      {formatPrice(plan.price_monthly, plan.price_yearly)}
                    </span>
                    {formatYearlyPrice(plan.price_yearly) && (
                      <span className="price-yearly">
                        {t("servicePlans.price.orPrefix")}{" "}
                        {formatYearlyPrice(plan.price_yearly)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="plan-description">
                  <p>{t(`servicePlans.plan.${plan.plan_name}.desc`)}</p>
                </div>

                <div className="plan-features">
                  <h4>{t("servicePlans.featuresTitle")}</h4>
                  <ul>
                    {getFeaturesList(plan).map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="plan-actions">
                <button
                  className={`select-plan-button ${
                    getPlanRelationship(plan) === "current" ? "current" : ""
                  } ${
                    getPlanRelationship(plan) === "upgrade" ? "upgrade" : ""
                  } ${
                    getPlanRelationship(plan) === "downgrade" ? "downgrade" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isPlanDisabled(plan)) {
                      handlePlanSelection(plan);
                    }
                  }}
                  disabled={isPlanDisabled(plan)}
                >
                  {getButtonText(plan)}
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* Only show back button when not in modal mode */}
      {!isModal && showBackButton && onBack && (
        <div className="plans-navigation">
          <button
            className="back-button"
            onClick={() => {
              // Find the currently selected plan by selectedPlanId
              const currentlySelectedPlan = plans.find(
                (plan) => plan.plan_id === selectedPlanId,
              );
              onBack(currentlySelectedPlan);
            }}
          >
            {t("servicePlans.btn.back")}
          </button>
        </div>
      )}

      {/* Go Back button shown only on mobile in modal mode */}
      {isModal && onClose && (
        <div className="modal-go-back-mobile">
          <button onClick={onClose}>{t("servicePlans.btn.goBack")}</button>
        </div>
      )}
    </div>
  );

  // Return modal wrapper if isModal is true — use Portal to render at body level
  // so the modal escapes any ancestor stacking contexts (header, alerts, etc.)
  if (isModal) {
    return ReactDOM.createPortal(
      <div
        className="modal-overlay service-plans-modal-overlay"
        onClick={onClose}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content service-plans-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          {renderPlansContent()}
        </div>
      </div>,
      document.body,
    );
  }

  // Return regular content if not modal
  return renderPlansContent();
};

export default ServicePlans;
